import { formatSignificantTwoDigitsGrouped } from "@src/utils/currencyUtils.js";

/**
 * 숫자를 로케일 형식으로 포맷팅
 * @param {number|string} value - 포맷팅할 값
 * @param {number} decimals - 소수점 자리수 (기본값: 0)
 * @returns {string} 포맷팅된 문자열
 */
export function formatNumber(value, decimals = 0) {
    if (value === null || value === undefined || isNaN(value)) return "0";
    return Number(value).toLocaleString("ko-KR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * 큰 숫자를 한글로 포맷팅 (억원, 만원 단위)
 * @param {number|string} value - 포맷팅할 값
 * @returns {string} 포맷팅된 문자열 (예: "1.5억원", "5만원")
 */
export function formatLargeNumber(value) {
    if (value === null || value === undefined || isNaN(value)) return "0원";
    const num = Number(value);
    if (num >= 100000000) {
        return `${(num / 100000000).toFixed(1)}억원`;
    } else if (num >= 10000) {
        return `${(num / 10000).toFixed(0)}만원`;
    } else {
        return `${num.toLocaleString("ko-KR")}원`;
    }
}

/**
 * 요약 금액 포맷팅 (유의미한 두 자리 숫자 그룹화)
 * @param {number|string} value - 포맷팅할 값
 * @returns {string} 포맷팅된 문자열
 */
export function formatSummaryAmount(value) {
    const num = Number(value);
    if (isNaN(num)) return "0원";
    return formatSignificantTwoDigitsGrouped(num);
}

/**
 * YYYYMM 형식을 "YYYY년 MM월" 형식으로 변환
 * @param {string} regYm - YYYYMM 형식의 문자열
 * @returns {string} 포맷팅된 문자열
 */
export function formatYearMonth(regYm) {
    if (!regYm) return "";
    const str = String(regYm);
    if (str.length === 6) {
        return `${str.substring(0, 4)}년 ${parseInt(str.substring(4, 6))}월`;
    }
    return str;
}

/**
 * 청구 연월 포맷팅 (빈 값일 경우 "-" 반환)
 * @param {string} billYearMonth - YYYYMM 형식의 문자열
 * @returns {string} 포맷팅된 문자열
 */
export function formatBillYearMonth(billYearMonth) {
    if (!billYearMonth) return "-";
    const str = String(billYearMonth);
    if (str.length === 6) {
        return `${str.substring(0, 4)}년 ${parseInt(str.substring(4, 6))}월`;
    }
    return str;
}

/**
 * YYYYMMDD 형식을 YYYY-MM-DD 형식으로 변환
 * @param {string} dateStr - YYYYMMDD 형식의 문자열
 * @returns {string} 포맷팅된 문자열
 */
export function formatBillingDate(dateStr) {
    if (!dateStr) return "-";
    const str = String(dateStr);
    // YYYYMMDD → YYYY-MM-DD
    if (str.length === 8) {
        return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
    }
    return str;
}

/**
 * YYYY-MM-DD 형식을 YYYY.MM.DD 형식으로 변환
 * @param {string} dateStr - YYYY-MM-DD 형식의 문자열
 * @returns {string} 포맷팅된 문자열
 */
export function formatDate(dateStr) {
    if (!dateStr) return "-";
    // YYYY-MM-DD 형식을 YYYY.MM.DD로 변환
    return dateStr.replace(/-/g, ".");
}

/**
 * 일별 날짜 포맷팅 (YYYYMMDD 또는 YYYY-MM-DD → "YYYY년 MM월 DD일")
 * @param {string} dateStr - 날짜 문자열
 * @returns {string} 포맷팅된 문자열
 */
export function formatDailyDate(dateStr) {
    if (!dateStr) return "-";
    
    // YYYYMMDD 형식인 경우
    if (dateStr.length === 8 && !dateStr.includes('-')) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
    }
    
    // YYYY-MM-DD 형식인 경우
    if (dateStr.length === 10 && dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-');
        return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
    }
    
    return dateStr;
}
